const DEFAULT_DEFINITIONS = {
  "0": "digit",
  a: "letter",
  "*": "any"
};
const LETTER = /\p{L}/u;
function resolveDefinition(char, definitions) {
  const custom = definitions?.[char];
  if (custom instanceof RegExp) {
    return {
      kind: "any",
      pattern: custom
    };
  }
  if (custom) {
    return {
      kind: custom
    };
  }
  const builtIn = DEFAULT_DEFINITIONS[char];
  return builtIn ? {
    kind: builtIn
  } : null;
}
function matchesInput(char, kind, pattern) {
  if (pattern) {
    return pattern.test(char);
  }
  if (kind === "digit") {
    return /\d/.test(char);
  }
  if (kind === "letter") {
    return LETTER.test(char);
  }
  return char.length > 0;
}
function parseMaskPatternInternal(pattern, definitions, optional = false) {
  const tokens = [];
  let index = 0;
  while (index < pattern.length) {
    const char = pattern[index];
    if (char === "\\") {
      const next = pattern[index + 1];
      if (next) {
        tokens.push({
          type: "literal",
          value: next
        });
      }
      index += 2;
      continue;
    }
    if (char === "{") {
      const end = pattern.indexOf("}", index + 1);
      if (end < 0) {
        tokens.push({
          type: "literal",
          value: "{"
        });
        index += 1;
        continue;
      }
      tokens.push({
        type: "fixed",
        value: pattern.slice(index + 1, end)
      });
      index = end + 1;
      continue;
    }
    if (char === "[") {
      const end = findClosingBracket(pattern, index + 1);
      if (end < 0) {
        tokens.push({
          type: "literal",
          value: "["
        });
        index += 1;
        continue;
      }
      const inner = parseMaskPatternInternal(pattern.slice(index + 1, end), definitions, true);
      tokens.push(...inner);
      index = end + 1;
      continue;
    }
    if (char === "]") {
      break;
    }
    const definition = resolveDefinition(char, definitions);
    if (definition) {
      tokens.push({
        type: "input",
        definition: char,
        kind: definition.kind,
        pattern: definition.pattern,
        optional
      });
      index += 1;
      continue;
    }
    tokens.push({
      type: "literal",
      value: char
    });
    index += 1;
  }
  return tokens;
}
function findClosingBracket(pattern, start) {
  let depth = 1;
  for (let index = start; index < pattern.length; index += 1) {
    if (pattern[index] === "[" && pattern[index - 1] !== "\\") {
      depth += 1;
    }
    if (pattern[index] === "]" && pattern[index - 1] !== "\\") {
      depth -= 1;
      if (depth === 0) {
        return index;
      }
    }
  }
  return -1;
}
export function parseMaskPattern(pattern, definitions) {
  return parseMaskPatternInternal(pattern, definitions);
}
export function maskInputSlotCount(tokens, options = {}) {
  return tokens.filter(token => token.type === "input" && (!options.requiredOnly || !token.optional)).length;
}
export function maskToPlaceholder(pattern, definitions) {
  const tokens = parseMaskPattern(pattern, definitions);
  let result = "";
  for (const token of tokens) {
    if (token.type === "literal" || token.type === "fixed") {
      result += token.value;
      continue;
    }
    result += "_";
  }
  return result;
}
export function extractMaskInput(masked, tokens) {
  let input = "";
  let cursor = 0;
  for (const token of tokens) {
    if (token.type === "fixed" || token.type === "literal") {
      if (masked.slice(cursor, cursor + token.value.length) === token.value) {
        cursor += token.value.length;
      }
      continue;
    }
    if (cursor >= masked.length) {
      continue;
    }
    const char = masked[cursor];
    if (matchesInput(char, token.kind, token.pattern)) {
      input += char;
      cursor += 1;
    }
  }
  return input;
}
export function formatWithMask(input, tokens) {
  if (!input) {
    return "";
  }
  let inputIndex = 0;
  let result = "";
  for (const token of tokens) {
    if (token.type === "fixed" || token.type === "literal") {
      result += token.value;
      continue;
    }
    if (inputIndex >= input.length) {
      if (token.optional) {
        continue;
      }
      break;
    }
    const char = input[inputIndex];
    if (!matchesInput(char, token.kind, token.pattern)) {
      if (token.optional) {
        continue;
      }
      break;
    }
    result += char;
    inputIndex += 1;
  }
  return result;
}
function isDigitOnlyPattern(tokens) {
  return tokens.every(token => token.type !== "input" || token.kind === "digit");
}
function looseInputFromRaw(raw, tokens) {
  const loose = raw.replace(/\D/g, "");
  const fixedDigits = tokens.filter(token => token.type === "fixed").map(token => token.value.replace(/\D/g, "")).join("");
  if (fixedDigits && loose.startsWith(fixedDigits)) {
    return loose.slice(fixedDigits.length);
  }
  return loose;
}
export function applyInputMask(raw, pattern, definitions) {
  const tokens = parseMaskPattern(pattern, definitions);
  const capacity = maskInputSlotCount(tokens);
  let input = extractMaskInput(raw, tokens);
  if (input.length === 0 && /\S/.test(raw) && isDigitOnlyPattern(tokens)) {
    input = looseInputFromRaw(raw, tokens);
  }
  if (input.length > capacity) {
    input = input.slice(0, capacity);
  }
  return formatWithMask(input, tokens);
}